/**
 * Sentry Configuration Constants
 *
 * Contains configuration values for Sentry error tracking and monitoring.
 */

/**
 * Sentry Initialization Configuration
 */
export const SENTRY_CONFIG = {
  /**
   * Sentry context tag for WebSDK iframe
   */
  CONTEXT_TAG: 'websdk-iframe',

  /**
   * Sample rate for performance tracing
   * 0.1 = 10% of transactions (balance between visibility and cost)
   */
  TRACES_SAMPLE_RATE: 0.1,

  /**
   * Sample rate for session replays (0.1 = 10%)
   */
  REPLAYS_SESSION_SAMPLE_RATE: 0.1,

  /**
   * Sample rate for replays on error (1.0 = 100%)
   * Always capture replay when an error occurs
   */
  REPLAYS_ON_ERROR_SAMPLE_RATE: 1.0,
} as const;

/**
 * URL Paths for verification flows
 */
export const VERIFICATION_PATHS = {
  /**
   * Face verification path
   */
  FACE_VERIFICATION: '/face-liveness',

  /**
   * Legal ID verification path (Veriff hosted flow)
   */
  LEGAL_ID_VERIFICATION: '/legal-id-verification',

  /**
   * Regula legal ID verification path (client-capture flow).
   */
  REGULA_LEGAL_ID_VERIFICATION: '/regula-legal-id-verification',

  /**
   * SSN card verification path
   */
  SSN_CARD_VERIFICATION: '/ssn-card-verification',
} as const;

/**
 * URL Parameter Names
 */
export const URL_PARAMS = {
  /**
   * Session token parameter name
   */
  SESSION_TOKEN: 'session_token',
} as const;

/**
 * Verification Flow Types
 */
export const FLOW_TYPES = {
  /**
   * Face verification flow
   */
  FACE_VERIFICATION: 'face-liveness',

  /**
   * Legal ID verification flow
   */
  LEGAL_ID: 'legal_id',

  /**
   * SSN card verification flow
   */
  SSN_CARD: 'ssn_card',

  /**
   * Unknown or unidentified flow
   */
  UNKNOWN: 'unknown',
} as const;

/**
 * Type for verification flow
 */
export type VerificationFlowType = typeof FLOW_TYPES[keyof typeof FLOW_TYPES];
